import { DEG, v3, compass, add, mul, dot } from "./vec3";
import { tablesOrigin } from "./contextPlanes";
const clamp = (x, a, b) => Math.max(a, Math.min(b, x));
const DEFAULT_ROW_COUNT = 5;
const MIN_DRAWN_ROWS = 2;

export function buildTerrain(slopeTilt, slopeAzimuth) {
  const tilt = Number(slopeTilt) || 0;
  const dir = tilt > 0 && slopeAzimuth != null && Number.isFinite(Number(slopeAzimuth))
    ? compass(Number(slopeAzimuth))
    : null;
  const grad = dir ? Math.tan(tilt * DEG) : 0;
  return {
    slopeDir: dir,
    slopeGrad: grad,
    groundZ: (x, y) => (dir ? -grad * (x * dir.x + y * dir.y) : 0),
  };
}

export function parcelFrame(tables, outline) {
  if (!Array.isArray(tables) || tables.length === 0) return null;
  const segs = tables
    .map((t) => ({
      start: { x: Number(t.start?.[0]), y: Number(t.start?.[1]) },
      end: { x: Number(t.end?.[0]), y: Number(t.end?.[1]) },
      columns: Number(t.columns) || 0,
      label: t.label || null,
    }))
    .filter((t) => [t.start.x, t.start.y, t.end.x, t.end.y].every(Number.isFinite) && t.columns > 0);
  if (!segs.length) return null;
  const anchor = outline?.frame_origin_xy;
  const fixed = Array.isArray(anchor) && anchor.length === 2 && anchor.every(Number.isFinite);
  const cx = fixed ? anchor[0] : segs.reduce((a, t) => a + (t.start.x + t.end.x) / 2, 0) / segs.length;
  const cy = fixed ? anchor[1] : segs.reduce((a, t) => a + (t.start.y + t.end.y) / 2, 0) / segs.length;
  const shift = (p) => ({ x: p.x - cx, y: p.y - cy });
  const ring = (r) =>
    Array.isArray(r) ? r.map((p) => shift({ x: Number(p[0]), y: Number(p[1]) })) : null;
  return {
    tables: segs.map((t) => ({ ...t, start: shift(t.start), end: shift(t.end) })),
    boundary: ring(outline?.boundary_xy),
    usable: ring(outline?.usable_xy),
    // Where the frame's origin is on the map, so another plane's tables can
    // be placed beside these by their offset on the ground.
    originLonLat: outline?.frame_origin_lonlat || tablesOrigin(tables),
  };
}

/** Corners of every row, plus the ground frame, in world coordinates. */
export function buildRows(
  scene,
  rotation,
  rowCount = DEFAULT_ROW_COUNT,
  explicitLength = null,
  spanRows = null,
  perRowModules = null
) {
  const u = compass(scene.axisAzimuth);                 // along the rows
  const c = compass(scene.axisAzimuth + 90);            // across the rows
  // Row length is drawn in proportion to the field, so the slice reads as a
  // patch of array rather than a few thin sticks: a fixed six modules looked
  // absurd beside a 40 m spread of pitch. Still a whole number of modules.
  const modulesAlong = explicitLength
    ? Math.max(1, Math.round(explicitLength / scene.moduleWidth))
    : clamp(Math.round((scene.pitch * rowCount * 0.7) / scene.moduleWidth), 6, 30);
  const rowLength = modulesAlong * scene.moduleWidth;
  const halfL = rowLength / 2;
  // The field's rows, module by module, when the caller knows them. The last
  // one is short when the modules do not divide - 1728 over 30 rows is 29 of
  // 58 and one of 46 - and a picture that drew thirty equal rows would be
  // drawing a plant of 1740.
  const perRow =
    Array.isArray(perRowModules) && perRowModules.length ? perRowModules : null;
  const cosR = Math.cos(rotation * DEG);
  const sinR = Math.sin(rotation * DEG);
  const halfSpanAcross = (scene.collectorWidth * cosR) / 2;

  // The axis sits high enough that the low edge is exactly at the ground
  // clearance, whichever way the row is rotated.
  const axisHeight =
    scene.clearance + (Math.abs(scene.collectorWidth * sinR)) / 2;

  const { slopeDir, slopeGrad, groundZ } = scene.terrain;

  // A row's two edges, each end standing on the ground UNDER THAT END. The
  // rows used to be built level at the height of the ground beneath their
  // middle, which on a hillside that falls along the row put one end in the
  // air and the other end underground: a 120 m table on a 4 degree slope is
  // 4.5 m out at each end. A corner below the ground has no shadow to project
  // (the ray never reaches the plane), and one such corner dropped the whole
  // row's shadow -- which is how a parcel on a slope lost every shadow in the
  // picture. Fixed structures follow the terrain along the row, so this is
  // also simply what is built.
  // ACROSS the row the tilt is the design's tilt from the horizontal, as in the
  // engine (pvlib's surface_tilt) and in PVsyst's plane tilt: both edges take
  // their height from the ground under the row's CENTRE LINE, so a slope across
  // the rows changes the post lengths, not the angle of the modules.
  const lowLift = axisHeight - (scene.collectorWidth * sinR) / 2;
  const highLift = axisHeight + (scene.collectorWidth * sinR) / 2;
  const edgePoint = (across, edgeOffset, along, lift, arrayOffset = null) => {
    const centre = add(add(mul(c, across), mul(u, along)),
      v3(arrayOffset?.eastM || 0, arrayOffset?.northM || 0, 0));
    const p = add(centre, mul(c, edgeOffset));
    const lowEdge = add(centre, mul(c, halfSpanAcross));
    return v3(p.x, p.y, groundZ(scene.roof ? lowEdge.x : centre.x, scene.roof ? lowEdge.y : centre.y) + lift);
  };
  const edges = (across, from, to, arrayOffset = null) => ({
    low: [edgePoint(across, halfSpanAcross, from, lowLift, arrayOffset), edgePoint(across, halfSpanAcross, to, lowLift, arrayOffset)],
    high: [edgePoint(across, -halfSpanAcross, from, highLift, arrayOffset), edgePoint(across, -halfSpanAcross, to, highLift, arrayOffset)],
  });

  const rows = [];
  if (scene.emptyRoof) return { rows, u, c, groundZ, slopeDir, slopeGrad,
    rowLength: 0, modulesAlong: 0, perRow: [] };

  // THE PARCEL'S TABLES, WHERE THEY REALLY ARE.
  // When the layout was fitted to a boundary, every table has a start and an
  // end on the ground, and the picture places it there: a row the boundary cut
  // in two is two tables on one line, the short rows sit where the land ran
  // out, and the whole field has the shape of the parcel. Drawn as fitted,
  // not sampled, so what is on screen is the layout that will be built.
  if (scene.parcel?.tables?.length) {
    // "Rows drawn" still means something here: the front N rows of the fitted
    // layout, every table of each. The field used to ignore the box in this
    // mode and draw all of them, which left a control on screen that did
    // nothing -- and on a 40-row parcel the rows became hairlines with no way
    // to zoom in on a few.
    const acrossOf = (t) => dot(v3((t.start.x + t.end.x) / 2, (t.start.y + t.end.y) / 2, 0), c);
    const rowPositions = Array.from(
      new Set(scene.parcel.tables.map((t) => Math.round(acrossOf(t) * 100) / 100))
    ).sort((a, b) => b - a); // front row first: +c is the side the array faces
    const keep = new Set(rowPositions.slice(0, Math.max(MIN_DRAWN_ROWS, rowCount)));
    scene.parcel.tables.forEach((t, k) => {
      if (!keep.has(Math.round(acrossOf(t) * 100) / 100)) return;
      const mid = v3((t.start.x + t.end.x) / 2, (t.start.y + t.end.y) / 2, 0);
      const across = dot(mid, c);
      const a0 = dot(v3(t.start.x, t.start.y, 0), u);
      const a1 = dot(v3(t.end.x, t.end.y, 0), u);
      const [from, to] = a0 <= a1 ? [a0, a1] : [a1, a0];
      rows.push({
        index: k,
        across,
        modules: t.columns,
        label: t.label,
        ...edges(across, from, to),
      });
    });
    const extent = rows.reduce((m, r) => Math.max(m, Math.abs(dot(r.low[0], u)), Math.abs(dot(r.low[1], u))), 0);
    return { rows, u, c, groundZ, slopeDir, slopeGrad,
             rowLength: extent * 2, modulesAlong, perRow: rows.map((r) => r.modules),
             boundary: scene.parcel.boundary, usable: scene.parcel.usable };
  }

  const n = Math.max(1, rowCount);
  // `spanRows` is how wide the field really is, when fewer rows are being built
  // than it has. The rows are then spread ACROSS that width rather than packed
  // at the true pitch - twelve rows at 4.8 m is a 53 m field, not a sample of a
  // 96 m one, and measuring an obstacle against the smaller field silently
  // changes the answer. Equal to `n` for the drawn slice, where the two agree.
  const span = Math.max(n, spanRows || n);
  const step = ((span - 1) * scene.pitch) / Math.max(1, n - 1);
  for (let k = 0; k < n; k += 1) {
    const offset = (k - (n - 1) / 2) * step;
    // Edge A on the +c side, edge B on the -c side. A positive rotation faces
    // +c, so A is the low edge and B the high one; a negative rotation (a
    // tracker pointing the other way) flips them without a special case.
    // Which row of the real field this drawn row stands for. Eight rows on
    // screen are a sample of twenty, so the last drawn row is the last row of
    // the field - and if that one is short, it is drawn short.
    const fieldIndex = perRow
      ? perRow.length === n
        ? k
        : Math.round((k * (perRow.length - 1)) / Math.max(1, n - 1))
      : k;
    const modules = perRow
      ? Math.max(1, perRow[Math.min(fieldIndex, perRow.length - 1)] || modulesAlong)
      : modulesAlong;
    // Rows of a field start at the same boundary; the last one stops short
    // where the modules run out. Centring the short row instead would put a
    // gap at both ends, which is not what the plant looks like.
    const tail = -halfL + modules * scene.moduleWidth;
    rows.push({
      index: k,
      across: offset,
      modules,
      ...edges(offset, -halfL, tail, scene.arrayOffset),
    });
  }
  return { rows, u, c, groundZ, slopeDir, slopeGrad, rowLength, modulesAlong, perRow };
}

